<template>
  <br />
  <div class="col-sm-12">

    <div v-if="loading" class="text-center py-4">
      <small>Cargando datos...</small>
    </div>

    <div v-if="error" class="alert alert-danger">{{ error }}</div>

    <div class="card detalle-contratante" v-if="!loading && data">
      <div class="card-header d-flex justify-content-between align-items-center">
        <div>
          <h5 class="mb-0">Detalle de Contratante</h5>
          <small class="text-muted">
            #{{ data.id }} · Creado {{ formatDateTime(data.created) }} · Actualizado {{ formatDateTime(data.updated) }}
          </small>
        </div>
        <button class="btn btn-sm btn-secondary" @click="$router.back()">
          <vue-feather type="arrow-left" size="14" class="me-1" /> Regresar
        </button>
      </div>

      <div class="card-body">

        <!-- Información Personal -->
        <section v-if="data.informacionPersonal">
          <h6 class="section-title"><vue-feather type="user" size="14" class="me-1" /> Información Personal</h6>
          <div class="row g-3">
            <div class="col-6 col-md-3">
              <small class="field-label">Nombre</small>
              <div class="field-value fw-semibold">{{ nombreCompleto }}</div>
            </div>
            <div class="col-6 col-md-3">
              <small class="field-label">RFC</small>
              <div class="field-value">{{ data.informacionPersonal.rfc || '—' }}</div>
            </div>
            <div class="col-6 col-md-3">
              <small class="field-label">CURP</small>
              <div class="field-value">{{ data.informacionPersonal.curp || '—' }}</div>
            </div>
            <div class="col-6 col-md-3">
              <small class="field-label">Fecha de Nacimiento</small>
              <div class="field-value">{{ formatDate(data.informacionPersonal.fechaNacimiento) }}</div>
            </div>
            <div class="col-6 col-md-3">
              <small class="field-label">Género</small>
              <div class="field-value">{{ data.informacionPersonal.genero || '—' }}</div>
            </div>
            <div class="col-6 col-md-3">
              <small class="field-label">Estado Civil</small>
              <div class="field-value">{{ data.informacionPersonal.estadoCivil || '—' }}</div>
            </div>
            <div class="col-6 col-md-3">
              <small class="field-label">Tipo de Sangre</small>
              <div class="field-value">{{ data.informacionPersonal.tipoSangre || '—' }}</div>
            </div>
            <div class="col-6 col-md-3">
              <small class="field-label">Color de Ojos</small>
              <div class="field-value">{{ data.informacionPersonal.colorOjos || '—' }}</div>
            </div>
            <div class="col-6 col-md-3">
              <small class="field-label">Estatura</small>
              <div class="field-value">{{ data.informacionPersonal.estatura != null ? data.informacionPersonal.estatura : '—' }}</div>
            </div>
            <div class="col-6 col-md-3">
              <small class="field-label">Peso</small>
              <div class="field-value">{{ data.informacionPersonal.peso != null ? data.informacionPersonal.peso : '—' }}</div>
            </div>
          </div>
        </section>

        <!-- Información Laboral -->
        <section v-if="data.informacionLaboral">
          <h6 class="section-title"><vue-feather type="briefcase" size="14" class="me-1" /> Información Laboral</h6>
          <div class="row g-3">
            <div class="col-6 col-md-3">
              <small class="field-label">Puesto</small>
              <div class="field-value">{{ data.informacionLaboral.puesto || '—' }}</div>
            </div>
            <div class="col-6 col-md-3">
              <small class="field-label">Área</small>
              <div class="field-value">{{ data.informacionLaboral.area || '—' }}</div>
            </div>
            <div class="col-6 col-md-3">
              <small class="field-label">Departamento</small>
              <div class="field-value">{{ data.informacionLaboral.departamento || '—' }}</div>
            </div>
            <div class="col-6 col-md-3">
              <small class="field-label">Fecha de Ingreso</small>
              <div class="field-value">{{ formatDate(data.informacionLaboral.fechaIngreso) }}</div>
            </div>
            <div class="col-6 col-md-3">
              <small class="field-label">Referencias</small>
              <div class="field-value">{{ data.informacionLaboral.referencias || '—' }}</div>
            </div>
          </div>

          <!-- Unidad -->
          <template v-if="data.informacionLaboral.unidad">
            <div class="subsection-title">Unidad</div>
            <div class="row g-3">
              <div class="col-6 col-md-3">
                <small class="field-label">Nombre</small>
                <div class="field-value fw-semibold">{{ data.informacionLaboral.unidad.nombre || '—' }}</div>
              </div>
              <div class="col-6 col-md-3">
                <small class="field-label">Clave</small>
                <div class="field-value">{{ data.informacionLaboral.unidad.clave || '—' }}</div>
              </div>
              <div class="col-6 col-md-3">
                <small class="field-label">Organización</small>
                <div class="field-value">{{ data.informacionLaboral.unidad.organizacion || '—' }}</div>
              </div>
              <div class="col-6 col-md-3">
                <small class="field-label">Domicilio</small>
                <div class="field-value">{{ data.informacionLaboral.unidad.domicilio || '—' }}</div>
              </div>
            </div>
          </template>
        </section>

        <!-- Domicilios -->
        <section>
          <h6 class="section-title"><vue-feather type="home" size="14" class="me-1" /> Domicilios</h6>
          <p v-if="!data.domicilios || data.domicilios.length === 0" class="text-muted mb-0 small">Sin domicilios registrados.</p>
          <div v-else class="entry-list">
            <div class="entry-card" v-for="(dom, idx) in data.domicilios" :key="idx">
              <span class="entry-field" v-for="entry in entries(dom)" :key="entry.label">
                <small class="field-label">{{ entry.label }}</small>
                <span class="field-value">{{ entry.value }}</span>
              </span>
            </div>
          </div>
        </section>

        <!-- Medios de Contacto -->
        <section>
          <h6 class="section-title"><vue-feather type="mail" size="14" class="me-1" /> Medios de Contacto</h6>
          <p v-if="!data.medios || data.medios.length === 0" class="text-muted mb-0 small">Sin medios de contacto registrados.</p>
          <div v-else class="entry-list">
            <div class="entry-card" v-for="(medio, idx) in data.medios" :key="idx">
              <span class="entry-field" v-for="entry in entries(medio)" :key="entry.label">
                <small class="field-label">{{ entry.label }}</small>
                <span class="field-value">{{ entry.value }}</span>
              </span>
            </div>
          </div>
        </section>

        <!-- Información Financiera -->
        <section class="last">
          <h6 class="section-title"><vue-feather type="dollar-sign" size="14" class="me-1" /> Información Financiera</h6>
          <p v-if="!data.informacionFinanciera || data.informacionFinanciera.length === 0" class="text-muted mb-0 small">Sin información financiera registrada.</p>
          <div v-else class="entry-list">
            <div class="entry-card" v-for="(fin, idx) in data.informacionFinanciera" :key="idx">
              <span class="entry-field" v-for="entry in entries(fin)" :key="entry.label">
                <small class="field-label">{{ entry.label }}</small>
                <span class="field-value">{{ entry.value }}</span>
              </span>
            </div>
          </div>
        </section>

      </div>
    </div>

  </div>
</template>

<script>
import api from '@/axios';

export default {
  name: 'detalleContratante',

  data() {
    return {
      data: null,
      loading: false,
      error: null,
    };
  },

  computed: {
    nombreCompleto() {
      const p = this.data?.informacionPersonal;
      if (!p) return '—';
      return [p.nombre, p.paterno, p.materno].filter(Boolean).join(' ');
    },
  },

  mounted() {
    this.fetchData();
  },

  methods: {
    async fetchData() {
      try {
        this.loading = true;
        this.error = null;
        const id = this.$route.params.id;
        const response = await api.get(`contratantes/${id}/detalle`);
        this.data = response.data;
      } catch (err) {
        console.error('Error fetching contratante:', err);
        this.error = 'No se pudo cargar el detalle del contratante.';
      } finally {
        this.loading = false;
      }
    },

    formatDate(val) {
      if (!val) return '—';
      const d = new Date(val);
      if (isNaN(d)) return val;
      const pad = n => String(n).padStart(2, '0');
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
    },

    formatDateTime(val) {
      if (!val) return '—';
      const d = new Date(val);
      if (isNaN(d)) return val;
      const pad = n => String(n).padStart(2, '0');
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },

    isIsoDateString(val) {
      if (typeof val !== 'string') return false;
      const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?$/;
      const isoRegexNoT = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
      return isoRegex.test(val) || isoRegexNoT.test(val);
    },

    friendlyLabel(key) {
      const base = key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ');
      return base.replace(/\b\w/g, l => l.toUpperCase());
    },

    // Convierte un objeto plano en pares {label, value} listos para mostrar,
    // sin depender de los nombres de campo exactos que devuelva la API
    // (evita el JSON.stringify crudo que había antes).
    entries(obj) {
      if (!obj || typeof obj !== 'object') return [];
      const skip = /^(id|.*Id|isActive|isMinimized)$/i;
      return Object.entries(obj)
        .filter(([key, value]) => !skip.test(key) && value !== null && value !== undefined && value !== '')
        .map(([key, value]) => ({
          label: this.friendlyLabel(key),
          value: this.isIsoDateString(value) ? this.formatDate(value) : String(value),
        }));
    },
  },
};
</script>

<style scoped>
.detalle-contratante section {
  padding: 1rem 0;
  border-bottom: 1px solid var(--bs-border-color, #e5e7eb);
}
.detalle-contratante section.last,
.detalle-contratante section:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}
.detalle-contratante section:first-child {
  padding-top: 0;
}
.section-title {
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--bs-secondary-color, #6c757d);
  margin-bottom: 0.75rem;
}
.subsection-title {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--bs-secondary-color, #6c757d);
  margin: 0.75rem 0 0.5rem;
}
.field-label {
  display: block;
  color: var(--bs-secondary-color, #6c757d);
  font-size: 0.72rem;
  line-height: 1.2;
}
.field-value {
  display: block;
  font-size: 0.875rem;
  line-height: 1.3;
}
.entry-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.entry-card {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.5rem;
  padding: 0.6rem 0.85rem;
  border: 1px solid var(--bs-border-color, #e5e7eb);
  border-radius: 0.375rem;
  background: var(--bs-tertiary-bg, #f8f9fa);
}
.entry-field {
  min-width: 7rem;
}
</style>
